import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import { WO_CATEGORIES, WO_PRIORITIES, woCategoryLabel, woPriorityLabel } from "../lib/workorders";

export function NewWorkOrderPage() {
  const navigate = useNavigate();

  const [buildingId, setBuildingId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [unitId, setUnitId] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("normal");
  const [assignedId, setAssignedId] = useState("");
  const [assignedDate, setAssignedDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [recurringType, setRecurringType] = useState("monthly");
  const [recurringDate, setRecurringDate] = useState("");
  const [adminAlertDate, setAdminAlertDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });
  const maintenanceQ = useQuery({
    queryKey: ["ref", "maintenance"],
    queryFn: () => api.reference.maintenance(),
  });
  const unitsQ = useQuery({
    queryKey: ["ref", "units", buildingId],
    queryFn: () => api.reference.units(Number(buildingId)),
    enabled: Boolean(buildingId),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.workOrders.create({
        buildingId: Number(buildingId),
        unitId: unitId ? Number(unitId) : null,
        title: title.trim(),
        description: description.trim(),
        category: category || null,
        priority,
        assignedMaintenanceId: assignedId ? Number(assignedId) : null,
        assignedDate: assignedDate || null,
        dueDate: dueDate || null,
        recurring,
        recurringType: recurring ? recurringType : null,
        recurringDate: recurring ? recurringDate || null : null,
        adminAlertDate: adminAlertDate || null,
      }),
    onSuccess: ({ id }) => navigate(`/work-orders/${id}`),
    onError: () => setError("Could not save the work order. Please try again."),
  });

  function onSave() {
    setError(null);
    if (!buildingId) return setError("Please choose a building.");
    if (!title.trim()) return setError("Please enter a title.");
    if (!description.trim()) return setError("Please enter a description.");
    createMut.mutate();
  }

  return (
    <Layout title="Add a Work Order">
      <p className="help-line">
        Pick the building first, then fill in the details. Only the building, title, and
        description are required.
      </p>
      <section className="card form-card">
        <label className="field">
          <span>Building</span>
          <select
            value={buildingId}
            onChange={(e) => {
              setBuildingId(e.target.value);
              setUnitId("");
            }}
          >
            <option value="">Choose a building…</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Kitchen sink is leaking"
          />
        </label>

        <label className="field">
          <span>Description</span>
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="field">
          <span>Unit / Location</span>
          <select
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            disabled={!buildingId}
          >
            <option value="">{buildingId ? "No specific unit" : "Choose a building first"}</option>
            {(unitsQ.data?.units ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                Unit {u.unitNumber}
                {u.floor ? ` (Floor ${u.floor})` : ""}
              </option>
            ))}
          </select>
        </label>

        <div className="two-col">
          <label className="field">
            <span>Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Not set</option>
              {WO_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {woCategoryLabel(cat)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {WO_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {woPriorityLabel(p)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Assigned maintenance person</span>
            <select value={assignedId} onChange={(e) => setAssignedId(e.target.value)}>
              <option value="">Unassigned</option>
              {(maintenanceQ.data?.maintenance ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Assigned date</span>
            <input
              type="date"
              value={assignedDate}
              onChange={(e) => setAssignedDate(e.target.value)}
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>To be completed by</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          <label className="field">
            <span>Alert admin on</span>
            <input
              type="date"
              value={adminAlertDate}
              onChange={(e) => setAdminAlertDate(e.target.value)}
            />
          </label>
        </div>

        <label className="checkbox-line">
          <input
            type="checkbox"
            checked={recurring}
            onChange={(e) => setRecurring(e.target.checked)}
          />
          <span>Recurring work order</span>
        </label>

        {recurring && (
          <div className="two-col">
            <label className="field">
              <span>Recurring type</span>
              <select value={recurringType} onChange={(e) => setRecurringType(e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
            <label className="field">
              <span>Next recurring date</span>
              <input
                type="date"
                value={recurringDate}
                onChange={(e) => setRecurringDate(e.target.value)}
              />
            </label>
          </div>
        )}

        {error && <p className="field-error">{error}</p>}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/work-orders")}
            disabled={createMut.isPending}
          >
            Cancel
          </button>
          <button className="btn btn-primary btn-xl" onClick={onSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Save work order"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
